// src/components/SalesDashboard/QuotationList.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Eye, RefreshCw, FileText, Clock, CheckCircle,
  Send, XCircle, AlertCircle, Search, ChevronLeft, ChevronRight, Loader,
} from 'lucide-react';
import api, { getStoredRole } from '../api';

// ── Styles ────────────────────────────────────────────────────────────────────
const S = `
  .ql-wrap { font-family: 'Inter', sans-serif; color: #000; }
  .ql-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 12px; }
  .ql-title { font-size: 22px; font-weight: 800; color: #1e293b; margin: 0; }

  .ql-tabs { display: flex; gap: 4px; background: #f1f5f9; padding: 4px; border-radius: 12px; flex-wrap: wrap; margin-bottom: 20px; }
  .ql-tab {
    padding: 7px 14px; border-radius: 9px; font-size: 12px; font-weight: 600;
    cursor: pointer; border: none; background: transparent; color: #1a1a1a; transition: all .15s;
    white-space: nowrap;
  }
  .ql-tab.active { background: white; color: #1e1b4b; box-shadow: 0 1px 4px rgba(0,0,0,.08); }
  .ql-tab:hover:not(.active) { color: #1e293b; }
  .ql-tab-count {
    margin-left: 6px; padding: 1px 7px; border-radius: 999px; font-size: 11px; font-weight: 700;
    background: #e2e8f0; color: #334155;
  }
  .ql-tab.active .ql-tab-count { background: #c7d2fe; color: #312e81; }

  .ql-search-row { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
  .ql-search-box { position: relative; flex: 1; max-width: 340px; }
  .ql-search-box input {
    width: 100%; padding: 9px 14px 9px 38px; border: 1.5px solid #e2e8f0; border-radius: 10px;
    font-size: 13px; outline: none; box-sizing: border-box; transition: border-color .15s;
  }
  .ql-search-box input:focus { border-color: #6366f1; }

  .ql-table-wrap { background: white; border-radius: 16px; border: 1px solid #e2e8f0; overflow: hidden; }
  .ql-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .ql-table thead tr { background: #f8fafc; border-bottom: 2px solid #e2e8f0; }
  .ql-table th { padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 700; color: #1a1a1a; text-transform: uppercase; letter-spacing: .5px; white-space: nowrap; }
  .ql-table td { padding: 14px 16px; border-bottom: 1px solid #f1f5f9; color: #1e293b; vertical-align: middle; }
  .ql-table tbody tr:last-child td { border-bottom: none; }
  .ql-table tbody tr:hover td { background: #fafbff; }

  .ql-badge {
    display: inline-block; padding: 3px 10px; border-radius: 20px;
    font-size: 11px; font-weight: 700; white-space: nowrap;
  }

  .ql-action-btn {
    display: inline-flex; align-items: center; gap: 5px; padding: 6px 12px;
    border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer;
    border: 1.5px solid transparent; transition: all .15s;
  }

  .ql-empty { text-align: center; padding: 56px 24px; color: #1a1a1a; }
  .ql-empty-icon { font-size: 48px; margin-bottom: 12px; }

  .ql-pagination { display: flex; align-items: center; gap: 8px; justify-content: flex-end; padding: 16px; border-top: 1px solid #f1f5f9; }
  .ql-page-btn {
    width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center;
    border: 1.5px solid #e2e8f0; background: white; cursor: pointer; font-size: 13px; font-weight: 600; color: #1a1a1a;
    transition: all .15s;
  }
  .ql-page-btn:hover:not(:disabled) { background: #f1f5f9; border-color: #c7d2fe; }
  .ql-page-btn:disabled { opacity: .4; cursor: not-allowed; }
  .ql-page-btn.active { background: #6366f1; border-color: #6366f1; color: white; }

  .ql-new-btn {
    display: flex; align-items: center; gap: 8px; padding: 10px 20px;
    background: linear-gradient(135deg,#6366f1,#4f46e5); color: white;
    border: none; border-radius: 10px; font-size: 13px; font-weight: 700; cursor: pointer;
    transition: opacity .2s;
  }
  .ql-new-btn:hover { opacity: .9; }

  .ql-refresh-btn {
    display: flex; align-items: center; gap: 6px; padding: 9px 14px;
    border: 1.5px solid #e2e8f0; background: white; color: #1a1a1a; border-radius: 10px;
    font-size: 13px; font-weight: 600; cursor: pointer;
  }
  .ql-refresh-btn:hover { background: #f8fafc; }
`;

const STATUS_CONFIG = {
  DRAFT:            { label: 'Draft',            bg: '#f1f5f9', color: '#475569', icon: FileText },
  PENDING_APPROVAL: { label: 'Pending Approval',  bg: '#fef3c7', color: '#b45309', icon: Clock },
  APPROVED:         { label: 'Approved',          bg: '#d1fae5', color: '#065f46', icon: CheckCircle },
  SENT_TO_CLIENT:   { label: 'Sent to Client',    bg: '#dbeafe', color: '#1d4ed8', icon: Send },
  SENT:             { label: 'Sent',              bg: '#dbeafe', color: '#1d4ed8', icon: Send },
  ACCEPTED:         { label: 'Accepted',          bg: '#d1fae5', color: '#047857', icon: CheckCircle },
  REJECTED:         { label: 'Rejected',          bg: '#fee2e2', color: '#b91c1c', icon: XCircle },
  CLIENT_ACCEPTED:  { label: 'Client Accepted',   bg: '#d1fae5', color: '#047857', icon: CheckCircle },
  CLIENT_REJECTED:  { label: 'Client Rejected',   bg: '#fee2e2', color: '#b91c1c', icon: XCircle },
  EXPIRED:          { label: 'Expired',           bg: '#f1f5f9', color: '#9ca3af', icon: AlertCircle },
};

const TABS = [
  { id: '', label: 'All' },
  { id: 'PENDING_APPROVAL', label: 'Pending Approval' },
  { id: 'APPROVED', label: 'Approved' },
  { id: 'CLIENT_ACCEPTED', label: 'Accepted' },
  { id: 'CLIENT_REJECTED', label: 'Rejected' },
];

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const normalizeStatus = (status) => {
  if (status === 'SENT') return 'SENT_TO_CLIENT';
  if (status === 'ACCEPTED') return 'CLIENT_ACCEPTED';
  if (status === 'REJECTED') return 'CLIENT_REJECTED';
  return status;
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: '#f1f5f9', color: '#64748b' };
  return (
    <span className="ql-badge" style={{ background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
};

const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
    <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

export default function QuotationList({ user, userRole, isAdmin: isAdminProp, onViewDetail, onCreateNew }) {
  const role = String(
    userRole ||
    getStoredRole() ||
    user?.role ||
    ''
  ).toUpperCase();
  const isAdmin = typeof isAdminProp === 'boolean'
    ? isAdminProp
    : ['ADMIN', 'ORG_ADMIN', 'SUPER_ADMIN', 'OWNER'].includes(role);

  const [tab, setTab] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [tabCounts, setTabCounts] = useState({});
  const perPage = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (tab) params.status = tab;
      const res = await api.get('/quotations', { params });
      let rows = res.data.data || [];
      if (search.trim()) {
        const q = search.toLowerCase();
        rows = rows.filter(r =>
          r.quotation_no?.toLowerCase().includes(q) ||
          r.customer_name?.toLowerCase().includes(q)
        );
      }
      setData(rows);
      setTotal(res.data.total || 0);
    } catch {
      setData([]);
    }
    setLoading(false);
  }, [tab, page, search]);

  const loadTabCounts = useCallback(async () => {
    const countKey = {
      '': 'all',
      PENDING_APPROVAL: 'pending',
      APPROVED: 'approved',
      CLIENT_ACCEPTED: 'accepted',
      CLIENT_REJECTED: 'rejected',
    };
    try {
      const requests = TABS.map(t => {
        const params = { page: 1, per_page: 1 };
        if (t.id) params.status = t.id;
        return api.get('/quotations', { params });
      });
      const responses = await Promise.all(requests);
      const next = {};
      TABS.forEach((t, index) => {
        next[countKey[t.id]] = responses[index]?.data?.total || 0;
      });
      setTabCounts(next);
    } catch {
      setTabCounts({});
    }
  }, []);

  const handleQuickApprove = async (qtId) => {
    setApprovingId(qtId);
    try {
      await api.post(`/quotations/${qtId}/approve`);
      await load();
      await loadTabCounts();
    } catch {
      // silently fail — user can open detail for error details
    }
    setApprovingId(null);
  };

  useEffect(() => { setPage(1); }, [tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTabCounts(); }, [loadTabCounts]);

  const totalPages = Math.ceil(total / perPage);
  const isPendingTab = tab === 'PENDING_APPROVAL';

  return (
    <>
      <style>{S}</style>
      <div className="ql-wrap">
        {/* Header */}
        <div className="ql-header">
          <h2 className="ql-title">Quotations</h2>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="ql-refresh-btn" onClick={() => { load(); loadTabCounts(); }}>
              <RefreshCw size={14} /> Refresh
            </button>
            <button className="ql-new-btn" onClick={onCreateNew}>
              <Plus size={16} /> New Quotation
            </button>
          </div>
        </div>

        {/* Status tabs */}
        <div className="ql-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`ql-tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              <span className="ql-tab-count">
                {{
                  '': tabCounts.all,
                  PENDING_APPROVAL: tabCounts.pending,
                  APPROVED: tabCounts.approved,
                  CLIENT_ACCEPTED: tabCounts.accepted,
                  CLIENT_REJECTED: tabCounts.rejected,
                }[t.id] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="ql-search-row">
          <div className="ql-search-box">
            <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              placeholder="Search quote no, customer…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div style={{ fontSize: 13, color: '#1a1a1a' }}>
            {total} quotation{total !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Table */}
        <div className="ql-table-wrap">
          {loading ? (
            <Spinner />
          ) : data.length === 0 ? (
            <div className="ql-empty">
              <div className="ql-empty-icon">📄</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>No quotations found</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>Create your first quotation using the button above.</div>
            </div>
          ) : (
            <>
              <table className="ql-table">
                <thead>
                  <tr>
                    <th>Quote No</th>
                    <th>Customer</th>
                    <th>Version</th>
                    <th>Total (₹)</th>
                    <th>Valid Until</th>
                    <th>Status</th>
                    <th>Submitted By</th>
                    {isPendingTab && <th>Days Waiting</th>}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map(qt => {
                    const daysWaiting = qt.created_at
                      ? Math.floor((Date.now() - new Date(qt.created_at)) / (1000 * 60 * 60 * 24))
                      : 0;
                    return (
                      <tr key={qt.id}>
                        <td>
                          <span style={{ fontWeight: 700, color: '#1e1b4b', fontFamily: 'monospace', fontSize: 13 }}>
                            {qt.quotation_no}
                          </span>
                          {qt.sent_at && (
                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                              Sent: {new Date(qt.sent_at).toLocaleDateString()}
                            </div>
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{qt.customer_name}</div>
                        </td>
                        <td>
                          <span style={{ background: '#eef2ff', color: '#6366f1', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                            V{qt.version}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontWeight: 700 }}>₹{fmt(qt.total_amount)}</span>
                        </td>
                        <td>
                          <ExpiryCell date={qt.valid_until} status={qt.status} />
                        </td>
                        <td>
                          <StatusBadge status={normalizeStatus(qt.status)} />
                        </td>
                        <td style={{ color: '#000' }}>
                          {qt.salesperson || '—'}
                        </td>
                        {isPendingTab && (
                          <td>
                            <span style={{
                              fontWeight: 600,
                              color: daysWaiting > 2 ? '#dc2626' : daysWaiting > 0 ? '#f59e0b' : '#64748b',
                              fontSize: 13,
                            }}>
                              {daysWaiting}d
                            </span>
                          </td>
                        )}
                        <td>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              className="ql-action-btn"
                              style={{ background: '#eef2ff', color: '#6366f1', borderColor: '#c7d2fe' }}
                              onClick={() => onViewDetail(qt.id)}
                            >
                              <Eye size={13} /> View
                            </button>
                            {isPendingTab && isAdmin && (
                              <button
                                className="ql-action-btn"
                                style={{ background: '#d1fae5', color: '#065f46', borderColor: '#6ee7b7' }}
                                disabled={approvingId === qt.id}
                                onClick={() => handleQuickApprove(qt.id)}
                              >
                                {approvingId === qt.id
                                  ? <Loader size={12} />
                                  : <CheckCircle size={12} />}
                                Approve
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="ql-pagination">
                  <span style={{ fontSize: 12, color: '#1a1a1a', marginRight: 8 }}>
                    Page {page} of {totalPages}
                  </span>
                  <button className="ql-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft size={14} />
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const p = page <= 3 ? i + 1 : page - 2 + i;
                    if (p < 1 || p > totalPages) return null;
                    return (
                      <button key={p} className={`ql-page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>
                        {p}
                      </button>
                    );
                  })}
                  <button className="ql-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function ExpiryCell({ date, status }) {
  if (!date) return <span style={{ color: '#94a3b8' }}>—</span>;
  const today = new Date();
  const d = new Date(date);
  const diff = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
  const expired = diff < 0 || status === 'EXPIRED';
  const urgent = diff >= 0 && diff <= 3;
  return (
    <span style={{ color: expired ? '#ef4444' : urgent ? '#f59e0b' : '#1e293b', fontWeight: urgent || expired ? 600 : 400 }}>
      {date}
      {expired && <span style={{ display: 'block', fontSize: 10 }}>Expired</span>}
      {!expired && urgent && <span style={{ display: 'block', fontSize: 10 }}>Expires in {diff}d</span>}
    </span>
  );
}
